<template>
  <div class="data-table">
    <!-- Toolbar -->
    <div class="table-toolbar">
      <div class="toolbar-left">
        <h4 v-if="title">{{ title }}</h4>
        <span class="record-count" v-if="total">{{ total }} records</span>
        <slot name="toolbar-left"></slot>
      </div>
      <div class="toolbar-right">
        <slot name="toolbar-right"></slot>
        <div class="search-field" v-if="searchable">
          <md-icon>search</md-icon>
          <input
              type="text"
              v-model="searchQuery"
              :placeholder="searchPlaceholder || 'Search...'"
              @input="onSearch"
              class="search-input"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <md-table>
        <md-table-row>
          <md-table-head v-for="col in columns" :key="col.field" :class="col.align || 'left'">
            {{ col.label }}
          </md-table-head>
          <md-table-head v-if="hasActions" class="text-center">Actions</md-table-head>
        </md-table-row>

        <md-table-row v-for="(item, index) in data" :key="item.id || index">
          <md-table-cell v-for="col in columns" :key="col.field" :class="col.align || 'left'">
            <slot :name="'cell-' + col.field" :item="item" :value="getValue(item, col.field)">
              {{ getValue(item, col.field) }}
            </slot>
          </md-table-cell>
          <md-table-cell v-if="hasActions" class="text-center">
            <div class="action-buttons">
              <slot name="actions" :item="item"></slot>
            </div>
          </md-table-cell>
        </md-table-row>

        <tr v-if="!data || data.length === 0">
          <td :colspan="columns.length + (hasActions ? 1 : 0)" class="empty-state">
            <md-icon>inbox</md-icon>
            <p>{{ emptyMessage || 'No data found' }}</p>
            <slot name="empty-actions"></slot>
          </td>
        </tr>
      </md-table>
    </div>

    <!-- Footer -->
    <div class="table-footer" v-if="showPagination">
      <div class="pagination-info">
        Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
      </div>
      <div class="pagination-controls">
        <slot name="pagination-left"></slot>
        <div class="per-page-wrapper" v-if="showPerPage">
          <label>Per page</label>
          <select v-model="perPage" @change="onPerPageChange" class="per-page-select">
            <option v-for="item in perPageOptions" :key="item" :value="item">
              {{ item }}
            </option>
          </select>
        </div>
        <pagination
            class="pagination-no-border pagination-success"
            v-model="currentPage"
            :per-page="perPage"
            :total="total"
            @change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';

export default {
  name: 'DataTable',
  components: { Pagination },
  props: {
    title: String,
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    showPerPage: {
      type: Boolean,
      default: true
    },
    perPageOptions: {
      type: Array,
      default: () => [5, 10, 25, 50]
    },
    defaultPerPage: {
      type: Number,
      default: 10
    },
    emptyMessage: {
      type: String,
      default: 'No data found'
    },
    hasActions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      perPage: this.defaultPerPage
    };
  },
  computed: {
    from() {
      return this.perPage * (this.currentPage - 1);
    },
    to() {
      let highBound = this.from + this.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    }
  },
  methods: {
    getValue(item, field) {
      if (!field) return null;
      if (field.includes('.')) {
        return field.split('.').reduce((obj, key) => obj && obj[key] !== undefined ? obj[key] : null, item);
      }
      return item[field];
    },
    onSearch() {
      this.currentPage = 1;
      this.$emit('search', this.searchQuery);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.$emit('page-change', page);
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.$emit('per-page-change', this.perPage);
    },
    reset() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.$emit('reset');
    }
  },
  watch: {
    data: {
      handler() {
        if (this.data.length === 0 && this.currentPage > 1) {
          this.currentPage = 1;
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.data-table {
  .table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 16px 0;
    flex-wrap: wrap;
    gap: 12px;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 12px;

      h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2d3748;
      }

      .record-count {
        font-size: 12px;
        color: #999;
        background: #f5f5f5;
        padding: 2px 10px;
        border-radius: 12px;
      }
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      .search-field {
        position: relative;
        width: 220px;

        .md-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          z-index: 1;
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 8px 12px 8px 36px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;

          &:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }

          &::placeholder {
            color: #999;
          }
        }
      }
    }
  }

  .table-wrapper {
    overflow-x: auto;

    .md-table {
      width: 100%;

      .md-table-head {
        font-weight: 600;
        color: #555;
        padding: 12px 16px;
        border-bottom: 2px solid #e8e8e8;

        &.text-center { text-align: center; }
        &.text-right { text-align: right; }
      }

      .md-table-cell {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;

        &.text-center { text-align: center; }
        &.text-right { text-align: right; }
      }

      .action-buttons {
        display: flex;
        justify-content: center;
        gap: 4px;

        .md-icon-button {
          width: 32px;
          height: 32px;
          min-width: 32px;
        }
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #999;

        .md-icon {
          font-size: 48px;
          width: 48px;
          height: 48px;
          color: #ddd;
        }

        p {
          margin: 12px 0 16px;
        }
      }
    }
  }

  .table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
    flex-wrap: wrap;
    gap: 12px;

    .pagination-info {
      font-size: 13px;
      color: #666;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      .per-page-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .per-page-select {
          padding: 6px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          background: white;
          cursor: pointer;

          &:focus {
            border-color: #667eea;
          }
        }
      }
    }
  }
}
</style>